<template>
  <tile class="opc-pbx opc-pbx__trial">
    <foldable v-model="isExpanded" :title="$t('billing.pbx.free_trial.title')">
      <template #head>
        <CardHeader :logo="icon" :title="$t('billing.pbx.free_trial.title')">
          <template #head-right-content>
            <zm-button v-if="isExpanded" data-testid="checkout-button-ztrial-1" type="text" @click="showPlanDetailDialog">
              {{ $t('billing.plan_details') }}
            </zm-button>
          </template>

          <!-- Collapsed content when foldable is closed -->
          <div class="opc-config__collapse" v-if="!isExpanded">
            <div class="opc-pbx__collapse-line1">
              {{ freeTrialCollapseLine1 }}
            </div>
            <div class="opc-pbx__collapse-line2">
              {{ freeTrialCollapseLine2 }}
            </div>
          </div>
        </CardHeader>

        <!-- Free Trial Benefits - Second row -->
        <div class="opc-pbx__trial-desc" v-if="isExpanded">
          <p class="opc-pbx__desc-text">
            <img src="@/assets/check_blue.svg" alt="Include" />
            {{ $t('billing.pbx.free_trial.desc1', [freeTrialDays]) }}
          </p>
          <p class="opc-pbx__desc-text">
            <img src="@/assets/check_blue.svg" alt="" />
            {{ $t('billing.pbx.free_trial.desc2') }}
          </p>
          <p class="opc-pbx__desc-text">
            <img src="@/assets/check_blue.svg" alt="" />
            {{ $t('billing.pbx.free_trial.desc3') }}
          </p>
        </div>
      </template>

      <div class="opc-pbx__main">
        <zm-form>
          <!-- How many licenses do you need -->
          <zm-form-item>
            <div class="opc-pbx__trial--cycle-item" data-opt-id="how-many-numbers">
              <div>
                <p class="label-title-1">
                  {{ $t('billing.pbx.how_many_license_need') }}
                  <zm-tooltip
                    v-if="showNumberList"
                    placement="top"
                    :content="$t('billing.pbx.number_selected_tips')"
                    @show="showNumberTips"
                  >
                    <tips-icon aria-label="licenses" data-testid="checkout-button-ztrial-info" />
                  </zm-tooltip>
                </p>
                <p class="opc-pbx__license-desc">
                  {{ $t('billing.pbx.license_phone_number_desc') }}
                </p>
              </div>
              <zm-radio-group
                class="opc-card-radio billing-cycle-radios"
                :aria-label="$t('billing.pbx.how_many_license_need')"
                v-model="pbxBillingCycle"
              >
                <zm-radio
                  v-for="plan in freeTrialCallingPlan"
                  :key="plan.plan_code || 'free-trial-plan'"
                  :label="plan.billing_cycle"
                  data-testid="checkout-radio-ztrial-license-1"
                >
                  <div class="opc-pbx__trial-card-content">
                    <p class="opc-pbx__trial-period-title">
                      {{ $t(`billing.billing_cycle.${plan.billing_cycle}`) }}
                      <span class="opc-pbx__trial-highlight">{{ $t('billing.pbx.free_trial.x_day_trial_desc', [plan.trialDays || 14]) }}</span>
                    </p>
                    <div class="opc-pbx__trial-pricing">
                      <span class="opc_hidden_element">current price {{ $money(0) }}, regular price {{ $money(plan.amount) }}</span>
                      <span class="opc-pbx__trial-price-current" aria-hidden="true">{{ $money(0) }}</span>
                      <span class="opc-pbx__trial-price-original" aria-hidden="true">{{ $money(plan.amount) }}</span>
                    </div>
                    <p class="opc-pbx__trial-billing-desc" v-html="$t('billing.pbx.free_trial.then_billed_monthly', [$money(plan.amount)])">
                    </p>
                  </div>
                </zm-radio>
              </zm-radio-group>
            </div>
            <!-- Local number list row -->
            <div class="opc-pbx__trial--number-item" data-opt-id="local-numbers">
              <div class="form-row" v-if="showNumberList && localNumberState.pickedNumberList.length">
                <div role="group">
                  <zm-tag
                    class="number-tag"
                    v-for="(numberItem, idx) in localNumberState.pickedNumberList"
                    :key="numberItem.label"
                    :closable="localNumberState.pickedNumberList.length > 1"
                    @close="onRemoveNumber('local', idx)"
                  >
                    {{ numberItem.displayName }}
                  </zm-tag>
                </div>
              </div>
              <!-- Local number operation row -->
              <div class="form-row" v-if="localOprationConf">
                <zm-button
                  v-if="localOprationConf.type !== 'loading'"
                  data-testid="checkout-button-ztrial-3"
                  :round="localOprationConf.round"
                  :type="localOprationConf.type"
                  @click="localOprationConf.onClick"
                  class="edit-numbers"
                >{{ localOprationConf.text }}</zm-button>
                <div v-else class="opration-loading" v-loading="true" spinner="opration-loading-icon"></div>
              </div>
            </div>
          </zm-form-item>
        </zm-form>
      </div>
    </foldable>
  </tile>
</template>

<script lang="jsx">
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import TipsIcon from '@/components/tips-icon/index.vue';
import CardHeader from '@/components/plan-card-header.vue';
import icon from '@/assets/plans/logo_pbx.svg';
import { mapGetters, mapMutations } from 'vuex';
import { computedPbxPlans, computedPbxCallingPlans } from '@/common/service/computers/plans/pbx';
import { PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import phoneNumberApi from '../mixin/phone-number-api';
import { localNumberStore, phoneNumberStorage } from '@/plans/ZOOM_PHONE/store';
import { PBX_REGIONS, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import SelectPhoneNumberDialog from '@/plans/ZOOM_PHONE/component/dialog/phone-number-select';
import PlanDetails from '@/plans/ZOOM_PHONE/component/dialog/plan-details.vue';
import { plansof } from '@/utils/chain';
import { values, isEmpty } from 'lodash';
import { $desc } from '@/components/service/config-desc';

export default {
  inject: ['$core'],
  data() {
    return {
      icon,
      isExpanded: true,
      numberTipsVisible: false,
      numberListLoading: true,
      localNumberState: localNumberStore.state,
      BILLING_CYCLE,
    };
  },
  components: {
    Foldable,
    Tile,
    TipsIcon,
    CardHeader,
  },
  mixins: [phoneNumberApi],
  computed: {
    ...mapGetters({
      'getSelectedPlans': 'getSelectedPlans',
      'planMapFlat': 'planMapFlat',
      'pbxBaseInfo': 'pbxBaseInfo',
      'pbxCallingPlanType': 'pbxCallingPlanType',
    }),
    pbxBillingCycle: {
      get() {
        return this.pbxBaseInfo.billingCycle;
      },
      set(newValue) {
        this.setPbxBillingCycle(newValue);
      }
    },
    pbxCallingPlan: {
      get() {
        return this.pbxCallingPlanType;
      },
      set(newValue) {
        this.setPbxCallingPlan(newValue);
      }
    },
    pbxBasePlanCode: {
      get() {
        return this.pbxBaseInfo.basePlanCode;
      },
      set(newValue) {
        this.setPbxBasePlanCode(newValue);
      }
    },
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    selectedCallingPlan() {
      const callingPlans = computedPbxCallingPlans(this.getSelectedPlans);
      return callingPlans.find(p =>
        p.region === PBX_REGIONS.US_AND_CANADA &&
        p.sub_type === PBX_SUB_TYPES.UNLIMITED &&
        p.billing_cycle === BILLING_CYCLE.MONTHLY &&
        p.isOnlineFreeTrial
      ) || callingPlans[0];
    },
    freeTrialCallingPlan() {
      // Get US/CA Unlimited Monthly free trial plan
      const plans = plansof(this.planMapFlat)
        .type(planTypes.ZOOM_PHONE)
        .region(PBX_REGIONS.US_AND_CANADA)
        .freeTrial()
        .get();

      return plans.filter(p =>
        p.sub_type === PBX_SUB_TYPES.UNLIMITED &&
        p.billing_cycle === BILLING_CYCLE.MONTHLY
      );
    },
    defaultFreeTrialPlan() {
      // Get the first (and only) free trial plan
      return this.freeTrialCallingPlan[0] || null;
    },
    freeTrialDays() {
      // Get trial days from selected calling plan, fallback to 14
      return this.selectedCallingPlan?.trialDays || 14;
    },
    freeTrialCollapseLine1() {
      if (!this.selectedCallingPlan) return '';
      const numberOfNumbers = this.selectedCallingPlan.cartInfo?.selected_number || 1;
      const priceAfterTrial = this.$money(this.selectedCallingPlan.amount);
      return this.$t('billing.pbx.free_trial.collapse_line1', [numberOfNumbers, priceAfterTrial]);
    },
    freeTrialCollapseLine2() {
      return this.$t('billing.pbx.free_trial.collapse_line2');
    },
    showNumberList() {
      return this.localNumberState.showNumberList && this.localNumberState.pickedNumberList.length > 0;
    },
    showLocalNumberSelectButton() {
      // Show "Select your numbers" button when no phone number list and no picked numbers
      // For free trial, we always show the number selection area
      return this.localNumberState.showNumberList &&
             !this.localNumberState.phoneNumberList.length &&
             (!this.localNumberState.searchTimes || this.localNumberState.searchTimes <= 0) &&
             !this.localNumberState.pickedNumberList.length;
    },
    showLocalNumberEditButton() {
      // Show "Edit numbers" button when we have phone numbers or picked numbers
      return (this.localNumberState.phoneNumberList.length || this.localNumberState?.pickedNumberList.length) &&
             this.localNumberState.showNumberList;
    },
    localOprationConf() {
      if (this.numberListLoading) {
        return {
          type: 'loading',
        };
      } else if (this.showLocalNumberSelectButton) {
        return {
          type: 'primary',
          onClick: () => this.handleSelectNumbers(),
          text: this.$t('billing.pbx.select_your_numbers'),
          round: true,
        };
      } else if (this.showLocalNumberEditButton) {
        const count = this.localNumberState.pickedNumberList.length;
        return {
          type: 'link',
          onClick: () => this.handleSelectNumbers(),
          text: this.$t('billing.pbx.edit_numbers', count, { count }),
          round: false,
        };
      }
      return null;
    },
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `zoom phone free trial - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
  methods: {
    ...mapMutations({
      'updateSelectPlan': 'updateSelectPlan',
      'setPbxBillingCycle': 'setPbxBillingCycle',
      'setPbxCallingPlan': 'setPbxCallingPlan',
      'setPbxBasePlanCode': 'setPbxBasePlanCode',
    }),
    updateCallingPlanQuantity(quantity) {
      const plan = this.defaultFreeTrialPlan;
      if (!plan) return;

      this.updateSelectPlan({
        ...plan,
        cartInfo: {
          selected_number: quantity,
        },
      });
    },
    async handleSelectNumbers() {
      if (!this.defaultFreeTrialPlan) return;

      this.$tracking.buyInteraction('zoom phone numbers', 'zoom phone free trial - select numbers');

      // Build checked phone number map from current state
      const checkedPhoneNumberMap = this.localNumberState.pickedNumberList.reduce((pre, item) => ({
        ...pre,
        [item.label]: item,
      }), {});

      // Open phone number select dialog
      const result = await this.selectPhoneNumberDialog.confirm({
        clearable: false, // Free trial must have at least 1 number, cannot clear all
        checkedPhoneNumberMap,
        searchByPhoneType: phoneNumberTypeMap.local,
        localCount: 1, // Free trial always 1 license
        tollFreeCount: 0, // Free trial doesn't support toll-free
        portCount: 0, // Free trial doesn't support porting
      });

      if (result && result.checkedPhoneNumberMap) {
        this.syncNumberListToCart(result);
      }
    },
    syncNumberListToCart(result) {
      const checkedPhoneNumberList = values(result.checkedPhoneNumberMap || {});
      const localNumbers = checkedPhoneNumberList.filter(item => item.type === phoneNumberTypeMap.local);

      // Free trial is limited to 1 license, so only allow 1 number
      const limitedNumbers = localNumbers.slice(0, 1);

      // Update local number state
      this.localNumberState.pickedNumberList = limitedNumbers;
      phoneNumberStorage.update('localNumberState', this.localNumberState);

      // Update plan quantity (always 1 for free trial)
      this.updateCallingPlanQuantity(1);

      // Show warning if user selected more than 1
      if (localNumbers.length > 1) {
        this.$message.warning(this.$t('billing.pbx.free_trial.max_numbers_warning', [1]));
      }
    },
    onRemoveNumber(type, idx) {
      if (type === 'local') {
        this.localNumberState.pickedNumberList.splice(idx, 1);
        phoneNumberStorage.update('localNumberState', this.localNumberState);
        // Free trial always 1 license
        this.updateCallingPlanQuantity(1);
      }
    },
    async showPlanDetailDialog() {
      this.$tracking.buyInteraction('plan details selected', 'zoom phone free trial');
      await this.planDetailsDialog.confirm({
        subType: this.selectedCallingPlan.sub_type,
        trialDays: this.freeTrialDays
      });
    },
    $init() {
      this.selectPhoneNumberDialog = new ServiceConfirm(SelectPhoneNumberDialog, this.$store);
      this.planDetailsDialog = new ServiceConfirm(PlanDetails, this.$store);
    },
    async initFreeTrialForm() {
      const plan = this.defaultFreeTrialPlan;
      if (!plan) return;

      // Initialize store values for free trial
      // Since free trial only has one option (Monthly), set it as default
      if (!this.pbxBillingCycle) {
        this.setPbxBillingCycle(plan.billing_cycle);
      }
      if (!this.pbxCallingPlan) {
        this.setPbxCallingPlan(plan.sub_type);
      }

      // Initialize selected plan if not already selected
      if (!this.selectedCallingPlan) {
        this.updateSelectPlan({
          ...plan,
          cartInfo: {
            selected_number: 1, // Free trial always 1 license
          },
        });
      }

      // Initialize phone numbers
      const phoneNumberCache = phoneNumberStorage.getState();
      if (isEmpty(phoneNumberCache)) {
        await this.fetchDefaultPhoneNumber();
        if (this._isDestroyed) {
          this.numberListLoading = false;
          return;
        }
        // If phone number list is empty, show "Select your numbers" button
        // Otherwise, populate default 1 number for free trial
        if (this.localNumberState.phoneNumberList.length > 0) {
          this.populateNumber(phoneNumberTypeMap.local, 1); // Free trial always 1 license
          // Show number list after populating
          this.localNumberState.showNumberList = true;
        } else {
          // Empty list, show "Select your numbers" button
          this.localNumberState.showNumberList = true;
        }
      } else {
        // Restore from cache
        this.localNumberState = Object.assign(this.localNumberState, phoneNumberCache.localNumberState || {});
        // Ensure at least 1 number is populated for free trial if we have numbers available
        if (this.localNumberState.pickedNumberList.length === 0 && this.localNumberState.phoneNumberList?.length > 0) {
          this.populateNumber(phoneNumberTypeMap.local, 1);
        }
        // Show number list if we have numbers or if we need to show select button
        if (this.localNumberState.pickedNumberList.length > 0 || this.localNumberState.phoneNumberList.length === 0) {
          this.localNumberState.showNumberList = true;
        }
      }
      // Set loading to false after initialization
      this.numberListLoading = false;
    },
    async fetchDefaultPhoneNumber() {
      const localNumberList = await this.searchLocalNumbers(this.getLocalNumberParamsWithIp());
      if (this._isDestroyed) {
        return;
      }
      this.localNumberState.phoneNumberList = localNumberList;
      this.localNumberState.cachePhoneNumberList = localNumberList;
      this.storagePhoneNumber();
    },
    populateNumber(type, license) {
      // For free trial, only support local numbers
      if (type !== phoneNumberTypeMap.local) return [];

      const phoneNumerState = this.localNumberState;
      const res = Array.from({ length: license }, () => {
        const nextNumber = phoneNumerState.cachePhoneNumberList
          .find(item => !phoneNumerState.pickedNumberList.find(item2 => item2.label === item.label));
        if (nextNumber) {
          phoneNumerState.pickedNumberList.push(nextNumber);
        }
        return nextNumber;
      }).filter(Boolean);

      this.storagePhoneNumber();
      return res;
    },
    storagePhoneNumber() {
      phoneNumberStorage.store({
        localNumberState: this.localNumberState,
      });
    },
    descRender() {
      return <p innerHTML={this.$t('bizPlus.pbx_fees_tips')}></p>;
    },
    showNumberTips() {
      this.$tracking.buyInteraction('zoom phone options', 'How many numbers do you need? - tooltip');
    },
  },
  mounted() {
    if (this.$context.is.buyTrial) {
      $desc(this.descRender());
    }
  },
  created() {
    this.$init();
    this.initFreeTrialForm();
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-modal {
  .opc-pbx__trial-desc {
    margin: 14px 0 24px 42px;
  }
}

.opc-pbx {
  &__trial.opc-pbx {
    .label-title-1 {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      margin-bottom: 4px;
    }
  }
  &__trial--cycle-item {
    display: flex;
    flex-direction: column;
  }
  &__trial--number-item {
    margin-top: 18px;
  }
  &__trial-desc {
    margin: 14px 0 24px 56px;

    @include media(tablet) {
      margin-left: 40px;
    }

    @include media(mobile) {
      margin-left: 16px;
    }
  }
  &__desc-text {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    margin: 4px 0;
    img {
      display: inline-block;
      margin-right: 8px;
    }
  }
  &__license-desc {
    color: #666487;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    margin: 4px 0 8px;
  }
  &__trial-card-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__trial-period-title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    .opc-pbx__trial-highlight {
      color: #247F40;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
    }
  }
  &__trial-pricing {
    display: flex;
    font-size: 24px;
    font-weight: 600;
    line-height: 28px;
    align-items: center;
    gap: 2px;
  }
  &__trial-price-current {
    color: #247F40;
    font-size: 28px;
    font-weight: 700;
  }
  &__trial-price-original {
    text-decoration: line-through;
    color: #2A2B2D;
  }
  &__trial-billing-desc {
    color: #2A2B2D;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;

    > b {
      font-weight: 600;
    }
  }

  .opc-config__collapse {
    padding-right: 80px;

    @include media(mobile) {
      padding-right: 0;
    }
  }

  &__collapse-line1 {
    color: var(--zoom-color-fill-neutral, #686F79);
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  &__collapse-line2 {
    color: var(--zoom-color-text-stronger-neutral, #222325);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }
}
</style>

